/**
 * Data aggregator for the Devices view (#999 Story 3.5).
 *
 * Calls the six repeatable-unit configuration hooks + location tags and builds
 * the Devices view model. Former chip-filter Location view fields were retired
 * in Phase 5.
 */

import { useMemo } from 'react';

import { useCoolingSystemConfigurations } from './queries/useCoolingSystemConfigurations';
import { useLargeUpsConfigurations } from './queries/useLargeUpsConfigurations';
import { useLiquidCoolingCduConfigurations } from './queries/useLiquidCoolingCduConfigurations';
import { useLocationTags } from './queries/useLocationTags';
import { usePowerDistributionConfigurations } from './queries/usePowerDistributionConfigurations';
import { useRackConfigurations } from './queries/useRackConfigurations';
import { useSmallUpsConfigurations } from './queries/useSmallUpsConfigurations';
import { useLocationTagNameLookup } from './useLocationTagNameLookup';
import { useResponsive } from './useResponsive';
import { resolveRecordTableLayout } from '../components/recordTable/resolveRecordTableLayout';
import { useOptionalLocationTagsDraft } from '../contexts/LocationTagsDraftContext';
import { buildDevicesViewModel } from '../utils/devicesViewModel';
import { draftTagsToGroupingInput } from '../utils/locationTagListGrouping';
import { isPersistedServerRequestId } from '../utils/locationTagRequestId';

import type { CoolingSystemConfiguration } from '../types/coolingSystem';
import type { LargeUpsConfiguration } from '../types/largeUps';
import type { LiquidCoolingCduConfiguration } from '../types/liquidCoolingCdu';
import type { PowerDistributionConfiguration } from '../types/powerDistribution';
import type { RackConfiguration } from '../types/rack';
import type { SmallUpsConfiguration } from '../types/smallUps';
import type { DevicesViewModel } from '../utils/devicesViewModel';

/** Optional edit-session overlays so Devices mirrors uncommitted order (Story 3.7). */
export interface DevicesUnitOverrides {
  racks?: readonly RackConfiguration[] | null;
  smallUps?: readonly SmallUpsConfiguration[] | null;
  largeUps?: readonly LargeUpsConfiguration[] | null;
  powerDistribution?: readonly PowerDistributionConfiguration[] | null;
  coolingSystems?: readonly CoolingSystemConfiguration[] | null;
  liquidCoolingCdus?: readonly LiquidCoolingCduConfiguration[] | null;
}

export interface UseLocationViewDataInput {
  requestId: string;
  enabledStepIds: readonly string[];
  unitOverrides?: DevicesUnitOverrides;
}

export interface UseLocationViewDataResult {
  isLoading: boolean;
  devicesViewModel: DevicesViewModel;
  resolveLocationTagName: (
    locationTagId: number | null | undefined,
  ) => string | null;
  /** True when any of the six unit queries or tags query failed. */
  isError: boolean;
}

export function useLocationViewData({
  requestId,
  enabledStepIds,
  unitOverrides,
}: UseLocationViewDataInput): UseLocationViewDataResult {
  // Create Request (`new-request`) must not hit /requests/:id/* — draft overrides + draft tags only.
  const enabledForFetch = isPersistedServerRequestId(requestId);
  const draftContext = useOptionalLocationTagsDraft();

  const racks = useRackConfigurations(requestId, enabledForFetch);
  const smallUps = useSmallUpsConfigurations(requestId, enabledForFetch);
  const largeUps = useLargeUpsConfigurations(requestId, enabledForFetch);
  const powerDistribution = usePowerDistributionConfigurations(
    requestId,
    enabledForFetch,
  );
  const coolingSystems = useCoolingSystemConfigurations(
    requestId,
    enabledForFetch,
  );
  const liquidCoolingCdus = useLiquidCoolingCduConfigurations(
    requestId,
    enabledForFetch,
  );
  const {
    data: locationTags = [],
    isLoading: tagsLoading,
    isError: tagsError,
  } = useLocationTags(requestId, enabledForFetch);

  const { resolveName: resolveLocationTagName } =
    useLocationTagNameLookup(requestId);

  const { dimensions } = useResponsive();
  /** Column set mirrors the step tables' own breakpoint. */
  const devicesLayout = resolveRecordTableLayout(dimensions.width);

  const draftTagInputs = useMemo(
    () => draftTagsToGroupingInput(draftContext?.draftTags ?? []),
    [draftContext?.draftTags],
  );

  const devicesViewModel = useMemo(() => {
    const tags = enabledForFetch
      ? locationTags.map(tag => ({ id: tag.id, name: tag.name }))
      : draftTagInputs;
    // Create/draft: never fall back to disabled query cache — only overrides.
    const racksUnits = enabledForFetch
      ? unitOverrides?.racks ?? racks.data ?? []
      : unitOverrides?.racks ?? [];
    const smallUpsUnits = enabledForFetch
      ? unitOverrides?.smallUps ?? smallUps.data ?? []
      : unitOverrides?.smallUps ?? [];
    const largeUpsUnits = enabledForFetch
      ? unitOverrides?.largeUps ?? largeUps.data ?? []
      : unitOverrides?.largeUps ?? [];
    const powerDistributionUnits = enabledForFetch
      ? unitOverrides?.powerDistribution ?? powerDistribution.data ?? []
      : unitOverrides?.powerDistribution ?? [];
    const coolingSystemUnits = enabledForFetch
      ? unitOverrides?.coolingSystems ?? coolingSystems.data ?? []
      : unitOverrides?.coolingSystems ?? [];
    const liquidCoolingCduUnits = enabledForFetch
      ? unitOverrides?.liquidCoolingCdus ?? liquidCoolingCdus.data ?? []
      : unitOverrides?.liquidCoolingCdus ?? [];
    return buildDevicesViewModel({
      tags,
      enabledStepIds,
      layout: devicesLayout,
      racks: racksUnits,
      smallUps: smallUpsUnits,
      largeUps: largeUpsUnits,
      powerDistribution: powerDistributionUnits,
      coolingSystems: coolingSystemUnits,
      liquidCoolingCdus: liquidCoolingCduUnits,
    });
  }, [
    enabledForFetch,
    locationTags,
    draftTagInputs,
    enabledStepIds,
    devicesLayout,
    racks.data,
    smallUps.data,
    largeUps.data,
    powerDistribution.data,
    coolingSystems.data,
    liquidCoolingCdus.data,
    unitOverrides,
  ]);

  const isLoading = enabledForFetch
    ? racks.isLoading ||
      smallUps.isLoading ||
      largeUps.isLoading ||
      powerDistribution.isLoading ||
      coolingSystems.isLoading ||
      liquidCoolingCdus.isLoading ||
      tagsLoading
    : false;

  const isError = enabledForFetch
    ? Boolean(racks.isError) ||
      Boolean(smallUps.isError) ||
      Boolean(largeUps.isError) ||
      Boolean(powerDistribution.isError) ||
      Boolean(coolingSystems.isError) ||
      Boolean(liquidCoolingCdus.isError) ||
      Boolean(tagsError)
    : false;

  return {
    isLoading,
    isError,
    devicesViewModel,
    resolveLocationTagName,
  };
}
